import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const TechnicalNamingCalculator = () => {
  const [nameData, setNameData] = useState({
    productName: '',
    version: '',
    architecture: '',
    environment: '',
    releaseDate: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const generateTechnicalName = () => {
    const { productName, version, architecture, environment, releaseDate } = nameData;
    
    if (!productName.trim() || !version.trim() || !architecture.trim()) {
      setError('Please enter all required fields');
      return;
    }

    // Generate technical name components
    const technicalName = {
      fullName: `${productName}-${version}-${architecture}${environment ? `-${environment}` : ''}${releaseDate ? `-${releaseDate.replace(/-/g, '')}` : ''}`,
      shortName: `${productName}-${version}`,
      buildName: `${productName}-${version}-${architecture}`
    };

    // Generate naming rules
    const namingRules = {
      product: 'Product name should be clear and descriptive',
      version: 'Version format: MAJOR.MINOR.PATCH',
      architecture: 'Architecture format: x86_64, arm64, etc.',
      environment: 'Environment format: dev, staging, prod',
      date: 'Release date format: YYYY-MM-DD'
    };

    // Generate example usage
    const exampleUsage = {
      installation: `Install ${technicalName.fullName} using package manager`,
      documentation: `Refer to ${productName} v${version} documentation`,
      build: `Build ${technicalName.buildName} for ${architecture}`
    };

    // Generate calculation steps
    const calculationSteps = [
      'Step 1: Prepare the name components',
      `Product Name: ${productName}`,
      `Version: ${version}`,
      `Architecture: ${architecture}`,
      `Environment: ${environment || 'None'}`,
      `Release Date: ${releaseDate || 'None'}`,
      '\nStep 2: Generate technical name',
      `Full Name: ${technicalName.fullName}`,
      `Short Name: ${technicalName.shortName}`,
      `Build Name: ${technicalName.buildName}`,
      '\nStep 3: Generate naming rules',
      `Product: ${namingRules.product}`,
      `Version: ${namingRules.version}`,
      `Architecture: ${namingRules.architecture}`,
      `Environment: ${namingRules.environment}`,
      `Date: ${namingRules.date}`
    ];

    // Generate chart data
    const chartData = {
      bar: {
        labels: ['Product', 'Version', 'Architecture', 'Environment', 'Date'],
        datasets: [{
          label: 'Technical Components',
          data: [1, 2, 3, environment ? 4 : 0, releaseDate ? 5 : 0],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            environment ? 'rgba(153, 102, 255, 0.5)' : 'rgba(0, 0, 0, 0)',
            releaseDate ? 'rgba(255, 159, 64, 0.5)' : 'rgba(0, 0, 0, 0)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            environment ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            releaseDate ? 'rgba(255, 159, 64, 1)' : 'rgba(0, 0, 0, 0)'
          ],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Product', 'Version', 'Architecture', 'Environment', 'Date'],
        datasets: [{
          data: [1, 1, 1, environment ? 1 : 0, releaseDate ? 1 : 0],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(255, 99, 132, 0.7)',
            'rgba(255, 159, 64, 0.7)',
            environment ? 'rgba(153, 102, 255, 0.7)' : 'rgba(0, 0, 0, 0)',
            releaseDate ? 'rgba(201, 203, 207, 0.7)' : 'rgba(0, 0, 0, 0)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 159, 64, 1)',
            environment ? 'rgba(153, 102, 255, 1)' : 'rgba(0, 0, 0, 0)',
            releaseDate ? 'rgba(201, 203, 207, 1)' : 'rgba(0, 0, 0, 0)'
          ],
          borderWidth: 1
        }]
      }
    };

    const newResults = {
      technicalName,
      namingRules,
      exampleUsage,
      calculationSteps
    };

    setResults(newResults);
    setChartData(chartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Technical Naming Calculator | Generate Technical Names</title>
        <meta name="description" content="Generate technical names for software products with our Technical Naming Calculator. Get accurate naming conventions for software development." />
        <meta name="keywords" content="technical naming, software naming, versioning, architecture naming, environment naming" />
        <meta property="og:title" content="Technical Naming Calculator | Generate Technical Names" />
        <meta property="og:description" content="Generate technical names for software products with our Technical Naming Calculator. Get accurate naming conventions for software development." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/technical-naming-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔧</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Technical Naming Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Generate Technical Names for Software Products</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator helps you generate technical names for software products using standardized naming conventions. It provides naming rules and examples for different components.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="productName">
            Product Name:
          </label>
          <input
            type="text"
            id="productName"
            name="productName"
            value={nameData.productName}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the product name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="version">
            Version:
          </label>
          <input
            type="text"
            id="version"
            name="version"
            value={nameData.version}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the version number (e.g., 1.0.0)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="architecture">
            Architecture:
          </label>
          <input
            type="text"
            id="architecture"
            name="architecture"
            value={nameData.architecture}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the architecture (e.g., x86_64, arm64)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="environment">
            Environment (optional):
          </label>
          <input
            type="text"
            id="environment"
            name="environment"
            value={nameData.environment}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter the environment (e.g., dev, staging, prod)"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="releaseDate">
            Release Date (optional):
          </label>
          <input
            type="date"
            id="releaseDate"
            name="releaseDate"
            value={nameData.releaseDate}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
          />
        </div>

        <button
          onClick={generateTechnicalName}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Generate Technical Name
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="calculator-section mt-12"
          >
            <div className="bg-white p-8 rounded-xl shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1  gap-6 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Technical Name</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Full Name</span>
                      <span className="font-medium">{results.technicalName.fullName}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Short Name</span>
                      <span className="font-medium">{results.technicalName.shortName}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Build Name</span>
                      <span className="font-medium">{results.technicalName.buildName}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Naming Rules</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Product</span>
                      <span className="font-medium">{results.namingRules.product}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Version</span>
                      <span className="font-medium">{results.namingRules.version}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Architecture</span>
                      <span className="font-medium">{results.namingRules.architecture}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Environment</span>
                      <span className="font-medium">{results.namingRules.environment}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Date</span>
                      <span className="font-medium">{results.namingRules.date}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Example Usage</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Installation</span>
                      <span className="font-medium">{results.exampleUsage.installation}</span>
                    </div>
                    <div className="flex items-center justify-between border-b pb-3">
                      <span className="text-gray-600">Documentation</span>
                      <span className="font-medium">{results.exampleUsage.documentation}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Build</span>
                      <span className="font-medium">{results.exampleUsage.build}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Component Chart</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData.bar}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Component Level'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Technical Component'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Technical Component Levels'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold mb-4">Naming Chart</h4>
                  <div className="h-96">
                    <Pie
                      data={chartData.pie}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Naming Components'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-12 bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Technical Naming Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Technical Naming Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Product Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the product name</li>
                <li>Input the version number (required)</li>
                <li>Input the architecture (required)</li>
                <li>Optionally add environment if applicable</li>
                <li>Optionally add release date if applicable</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Generate Technical Name</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Generate Technical Name" button</li>
                <li>Review the generated technical name</li>
                <li>Check the naming rules</li>
                <li>Verify the example usage</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the full name, short name, and build name</li>
                <li>Review the naming rules for each component</li>
                <li>Verify the example usage for different scenarios</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is technical naming?</h3>
              <p className="text-gray-700">
                Technical naming is a standardized system used to name software products and components. It includes product name, version, architecture, and other technical specifications.
              </p>
            </div>

            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What components are required?</h3>
              <p className="text-gray-700">
                The minimum required components are:
                <br/><br/>
                - Product Name: Clear and descriptive name
                <br/>
                - Version: Follows semantic versioning (MAJOR.MINOR.PATCH)
                <br/>
                - Architecture: System architecture (e.g., x86_64, arm64)
              </p>
            </div>

            <div className="border-l-4 border-yellow-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">Why is technical naming important?</h3>
              <p className="text-gray-700">
                Technical naming helps in:
                <br/><br/>
                - Unique identification of software versions
                <br/>
                - Clear communication among developers
                <br/>
                - Automated build and deployment processes
                <br/>
                - Package management and distribution
              </p>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What is semantic versioning?</h3>
              <p className="text-gray-700">
                Semantic versioning (SemVer) is a versioning system that uses three numbers:
                <br/><br/>
                MAJOR.MINOR.PATCH
                <br/><br/>
                - MAJOR: Breaking changes
                <br/>
                - MINOR: Backwards compatible features
                <br/>
                - PATCH: Backwards compatible bug fixes
              </p>
            </div>

            <div className="border-l-4 border-red-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">What are common architectures?</h3>
              <p className="text-gray-700">
                Common architectures include:
                <br/><br/>
                - x86_64 (64-bit Intel/AMD)
                <br/>
                - arm64 (64-bit ARM)
                <br/>
                - armv7 (32-bit ARM)
                <br/>
                - i386 (32-bit Intel/AMD)
              </p>
            </div>

            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-lg font-semibold mb-2">How do I choose an environment?</h3>
              <p className="text-gray-700">
                Common environments are:
                <br/><br/>
                - dev: Development environment
                <br/>
                - staging: Pre-production testing
                <br/>
                - prod: Production environment
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Generate standardized technical names</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Support for semantic versioning</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Architecture-specific naming</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Environment-based naming</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-blue-600 rounded-full mr-2 mt-1"></span>
                  <span>Release date integration</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-green-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Consistent naming across products</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Easy identification of software versions</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Streamlined package management</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Automated build and deployment</span>
                </li>
                <li className="flex items-start">
                  <span className="h-3 w-3 bg-green-600 rounded-full mr-2 mt-1"></span>
                  <span>Improved developer collaboration</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Technical Naming <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Technical naming is a crucial aspect of software development that helps in uniquely identifying software products and their versions. It follows standardized conventions to ensure consistency across different systems and environments.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Product Name:</span> Clear and descriptive identifier
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Version:</span> Follows semantic versioning
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Architecture:</span> System-specific identifier
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Environment:</span> Deployment context
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-purple-600 rounded-full mr-2"></span>
              <span className="font-medium">Release Date:</span> Version timestamp
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Technical names are typically used in package management systems, build scripts, and deployment configurations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TechnicalNamingCalculator;